import { Draft, produce } from 'immer';
import clamp from 'lodash/clamp';

import * as TYPES from '@app/store/actionTypes';
import FieldType from '@app/store/FieldType';
import {
  scheduledDocumentPreviewDataFetched,
  setupTemplateByDraft,
  TemplateAction,
} from '@app/store/template/actions';
import FieldPosition from '@app/store/template/FieldPosition';

export interface TemplateFieldPositionsState {
  [id: string]: FieldPosition;
}

const initialState: TemplateFieldPositionsState = {};

const reducer = produce((draft: Draft<TemplateFieldPositionsState>, action: TemplateAction) => {
  switch (action.type) {
    case TYPES.TEMPLATE_CLEAR_ALL_STATE:
    case TYPES.TEMPLATE_INFORMATION_FETCH_REQUEST:
      return initialState;
    case scheduledDocumentPreviewDataFetched.type:
    case TYPES.TEMPLATE_INFORMATION_FETCH_SUCCESS: {
      const { participants } = action.payload;
      participants.forEach((participant) => {
        const { fields } = participant;
        const { checkboxGroups, signatureFields, textFields } = fields;

        checkboxGroups.forEach((checkboxGroup) => {
          draft[checkboxGroup.id] = {
            page: checkboxGroup.page,
            relativeX: 0,
            relativeY: 0,
          };
          checkboxGroup.children.forEach((checkboxField) => {
            draft[checkboxField.id] = {
              page: checkboxField.page,
              relativeX: checkboxField.x,
              relativeY: checkboxField.y,
            };
          });
        });
        signatureFields.forEach((signatureField) => {
          draft[signatureField.id] = {
            page: signatureField.page,
            relativeX: signatureField.x,
            relativeY: signatureField.y,
          };
        });
        textFields.forEach((textField) => {
          draft[textField.id] = {
            page: textField.page,
            relativeX: textField.x,
            relativeY: textField.y,
          };
        });
      });
      return draft;
    }
    case setupTemplateByDraft.type: {
      action.payload.participants.forEach((participant) => {
        participant.fields.forEach((field) => {
          const { page, x: relativeX, y: relativeY } = field.position;
          draft[field.id] = {
            page,
            relativeX,
            relativeY,
          };
          if (field.type === FieldType.CHECKBOX_GROUP) {
            field.children.forEach((child) => {
              draft[child.id] = {
                page: child.position.page,
                relativeX: child.position.x,
                relativeY: child.position.y,
              };
            });
          }
        });
      });

      return draft;
    }
    case TYPES.TEMPLATE_FIELD_CREATE: {
      const { id, page, relativeX, relativeY } = action.payload.meta;
      draft[id] = {
        page,
        relativeX,
        relativeY,
      };
      return draft;
    }
    case TYPES.TEMPLATE_FIELD_POSITION_CHANGE: {
      const { id, relativeX, relativeY } = action.payload;
      draft[id].relativeX = clamp(relativeX, 0, 1);
      draft[id].relativeY = clamp(relativeY, 0, 1);
      return draft;
    }
    case TYPES.TEMPLATE__FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE:
    case TYPES.TEMPLATE_PARTICIPANT_DELETE: {
      const { fieldIds } = action.payload;
      fieldIds.forEach((key) => {
        if (key in draft) {
          delete draft[key];
        }
      });
      return draft;
    }
    default:
      return draft;
  }
}, initialState);

export default reducer;
